import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import {
  getNumberFormat,
  integerToCurrency,
} from '@actual-app/core/shared/util';

import { cartao, cor, texto } from '#components/finapp/estilo';
import type { MesDaSerie } from '#components/finapp/motor/historicoMensal';
import { useLocale } from '#hooks/useLocale';

type Props = {
  serie: MesDaSerie[];
  /** O mês que a tela está mostrando, destacado na série. */
  mesDestacado: string;
  /**
   * ⚠️⚠️ Revisão final da branch (Finding 1, CRITICAL) — vêm de
   * `useHistorico`, via `Inicio.tsx`. Sem eles, uma falha na consulta do
   * histórico fazia `montarSerie` devolver 6 meses ZERADOS — 6 barras de
   * 1px, que o JSDoc deste componente define como "foi zero" — sem nenhum
   * aviso de que a leitura falhou. Mesma ORDEM de estados que
   * `inicio/ResumoCompromissos.tsx`/`inicio/GastoMedioPorTipo.tsx`: erro
   * vence, depois carregando, só então as barras.
   */
  carregando?: boolean;
  erro?: Error | null;
};

const ALTURA = 72;

/**
 * Barras da renda mês a mês — o bloco que existe porque a renda do dono é
 * VARIÁVEL. "Recebi R$ 20 mil" não diz nada sozinho; "recebi R$ 20 mil depois
 * de três meses de R$ 12 mil" diz tudo.
 *
 * ⚠️ `View` com `backgroundColor`/altura em vez de biblioteca de gráfico
 * (nem SVG, ao final — divergência do brief, que descrevia "SVG inline": são
 * N retângulos e nenhuma interação, o mesmo componente de layout do resto do
 * arquivo já resolve). Puxar uma dependência de chart para isto seria peso de
 * bundle por nada.
 *
 * ⚠️ Mês zerado vira barra de altura 1px, não altura zero: uma barra ausente
 * lê-se como "não sei", e uma de 1px lê-se como "foi zero" — que é a verdade
 * que `montarSerie` apurou.
 *
 * ⚠️ Divergência do brief: `monthUtils.format` recebe `locale` como 3º
 * argumento (como `Inicio.tsx` já faz para o aviso da REGRA 5) — sem isso os
 * meses saem em inglês. O brief não passava `locale`; chamado aqui via
 * `useLocale()`, o mesmo hook que `Cabecalho.tsx` já chama por conta própria
 * (não recebido por prop do pai).
 *
 * ⭐⭐ Revisão final da branch (Finding 8, "a maior melhoria por linha
 * escrita") — `montarSerie` já calculava `entrou`/`gastou`/`guardou` para
 * cada mês da janela, e este bloco plotava só `entrou`. O QUINTO passo da
 * história do dono ("quanto consegui guardar") ficava um número solto do
 * mês corrente; sem a SEQUÊNCIA ao lado, "guardei R$2.000" não diz se é
 * ótimo ou péssimo. `guardou` entra como uma SEGUNDA barra, mais estreita,
 * ao lado da de `entrou`, dentro da mesma coluna do mês — não sobreposta
 * (ficaria ambígua sobre qual altura pertence a qual série) nem numa linha
 * separada por SVG (peso de dependência por uma única série, contra a
 * mesma razão que já vetou biblioteca de gráfico para `entrou`).
 *
 * ⚠️⚠️⚠️ CORREÇÃO CRÍTICA (validação com dado real, 22/09/2026) — as duas
 * séries usam UMA ESCALA SÓ (`maiorValor`, o maior entre TODO `entrou` e
 * TODO `guardou` da janela). A versão anterior dava a cada série a sua
 * PRÓPRIA escala (`maiorEntrada`/`maiorGuardado` independentes) — e isso é
 * exatamente o que fazia o gráfico MENTIR: as duas barras ficam lado a
 * lado, na mesma coluna, na mesma baseline, com legenda de bolinhas
 * coloridas — a gramática visual universal de "compare estas duas pela
 * altura". Com dado real de setembro, "entrou R$5.967" e "guardei R$800"
 * (13,4% daquilo) saíam com a MESMA ALTURA na tela, porque cada uma
 * enchia 100% do seu próprio eixo. Batendo o olho — antes de qualquer
 * número escrito — parecia "guardei quase tudo que entrou". Era falso, e
 * o único jeito de descobrir o valor real era o `title` (tooltip), que não
 * existe em toque — e o dono usa o celular.
 *
 * Com a escala única, uma barra de "guardei" pequena ao lado de uma de
 * "entrou" alta é exatamente a informação verdadeira e útil: o dono quer
 * saber quanto SOBROU do que entrou, e uma barrinha curta conta essa
 * história com honestidade. O preço é que a SEQUÊNCIA de "guardei" (o
 * ARCO mês a mês que a Task 9 original queria mostrar) fica mais achatada
 * quando `guardou` é sempre uma fração pequena de `entrou` — aceito de
 * propósito: legibilidade da PROPORÇÃO (não mentir) vence a legibilidade
 * do ARCO. Os valores numéricos abaixo das barras (novos nesta correção,
 * ver `alturaGuardou`/data-testid `valor-guardou-*`) cobrem essa perda:
 * mesmo com a barra achatada, o número mostra a variação mês a mês.
 */
export function RendaDosMeses({
  serie,
  mesDestacado,
  carregando = false,
  erro = null,
}: Props) {
  const { t } = useTranslation();
  const locale = useLocale();
  /**
   * ⚠️⚠️⚠️ Escala ÚNICA para as duas séries — ver o JSDoc do componente
   * (a correção crítica de 22/09/2026). `Math.max` sobre os dois campos das
   * duas séries juntos, não um por série.
   */
  const maiorValor = Math.max(
    ...serie.map(m => m.entrou),
    ...serie.map(m => m.guardou),
    1,
  );
  /**
   * Valor de "guardei" sem casas decimais, pra caber abaixo da barra em
   * 390px — ver `alturaGuardou`/`data-testid="valor-guardou-*"` mais
   * abaixo.
   */
  const formatoSemCentavos = getNumberFormat({ hideFraction: true }).formatter;

  return (
    <View style={{ ...cartao, gap: 12 }} data-testid="renda-dos-meses">
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <Text style={texto.rotuloCartao}>{t('O que entrou, mês a mês')}</Text>
        {!erro && !carregando && (
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <Legenda cor={cor.entrada} rotulo={t('entrou')} />
            <Legenda cor={cor.investimento} rotulo={t('guardei')} />
          </View>
        )}
      </View>

      {erro ? (
        <Text
          data-testid="renda-dos-meses-erro"
          style={{ ...texto.corpo, color: cor.saida }}
        >
          {t('Não consegui ler seu histórico: {{erro}}', {
            erro: erro.message,
          })}
        </Text>
      ) : carregando ? (
        <Text data-testid="renda-dos-meses-carregando" style={texto.corpo}>
          <Trans>Carregando…</Trans>
        </Text>
      ) : (
        <>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-end',
              gap: 8,
              height: ALTURA,
            }}
          >
            {serie.map(m => {
              const destaque = m.mes === mesDestacado;
              const alturaEntrou = Math.max(
                1,
                Math.round((m.entrou / maiorValor) * ALTURA),
              );
              const alturaGuardou = Math.max(
                1,
                Math.round((m.guardou / maiorValor) * ALTURA),
              );

              return (
                <View
                  key={m.mes}
                  style={{
                    flexGrow: 1,
                    flexDirection: 'row',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    gap: 3,
                  }}
                  title={`${m.mes}: entrou ${integerToCurrency(m.entrou)} · guardei ${integerToCurrency(m.guardou)}`}
                  data-testid={`barra-${m.mes}`}
                >
                  <View
                    style={{
                      flexGrow: 1,
                      height: alturaEntrou,
                      borderRadius: 4,
                      backgroundColor: destaque ? cor.acento : cor.entradaFraca,
                    }}
                    data-testid={`barra-entrou-${m.mes}`}
                  />
                  <View
                    style={{
                      flexGrow: 1,
                      height: alturaGuardou,
                      borderRadius: 4,
                      backgroundColor: destaque
                        ? cor.investimento
                        : cor.investimentoFraca,
                    }}
                    data-testid={`barra-guardou-${m.mes}`}
                  />
                </View>
              );
            })}
          </View>

          {/*
           * ⚠️⚠️⚠️ Valor de "guardei" por mês, visível — não só no `title`
           * (validação com dado real, 22/09/2026: tooltip não existe em
           * toque, e o dono usa o celular). Só "guardei", não os dois: em
           * 390px, com 6 colunas de ~49px cada dentro do cartão, os dois
           * números lado a lado cortariam ou quebrariam de forma feia. Entre
           * "entrou" e "guardei", "guardei" é o passo que hoje é INVISÍVEL
           * (a barra pode ficar achatada de propósito, ver o JSDoc do
           * componente) — "entrou" já se lê pela altura da própria barra,
           * quase sempre a mais alta da coluna. Sem casas decimais
           * (`formatoSemCentavos`) pra caber; o `title` acima mantém o valor
           * exato com centavos pra quem passa o mouse.
           */}
          <View style={{ flexDirection: 'row', gap: 8 }}>
            {serie.map(m => {
              const destaque = m.mes === mesDestacado;
              return (
                <Text
                  key={m.mes}
                  data-testid={`valor-guardou-${m.mes}`}
                  style={{
                    ...texto.rotuloTabela,
                    flexGrow: 1,
                    textAlign: 'center',
                    color: destaque ? cor.investimento : cor.tintaFraca,
                  }}
                >
                  {integerToCurrency(m.guardou, formatoSemCentavos)}
                </Text>
              );
            })}
          </View>

          <View style={{ flexDirection: 'row', gap: 8 }}>
            {serie.map(m => (
              <Text
                key={m.mes}
                style={{
                  ...texto.rotuloTabela,
                  flexGrow: 1,
                  textAlign: 'center',
                  color: m.mes === mesDestacado ? cor.tinta : cor.tintaFraca,
                }}
              >
                {monthUtils.format(m.mes, 'MMM', locale)}
              </Text>
            ))}
          </View>
        </>
      )}
    </View>
  );
}

/** Uma bolinha colorida + rótulo — a legenda das duas séries de barras. */
function Legenda({ cor: corBolinha, rotulo }: { cor: string; rotulo: string }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
      {/*
       * ⚠️⚠️ `flexShrink: 0` (validação com dado real, 22/09/2026) — sem
       * ele, em 390px este `View` de 8x8 competia por espaço com o texto do
       * rótulo dentro da legenda (`flexDirection: 'row', gap: 10` no card,
       * que também tem que caber "entrou" e "guardei" lado a lado) e
       * colapsava pra largura ZERO (medido: `offsetWidth` 0) — sobrava só o
       * texto, sem a cor que diz qual série é qual.
       */}
      <View
        style={{
          width: 8,
          height: 8,
          borderRadius: 4,
          backgroundColor: corBolinha,
          flexShrink: 0,
        }}
      />
      <Text style={{ ...texto.rotuloTabela, color: cor.tintaFraca }}>
        {rotulo}
      </Text>
    </View>
  );
}
